import { memo, useCallback, useMemo } from 'react';

import { type BreadcrumbsItemType } from '@air/component-breadcrumbs';
import { LinkIcon } from '@air/design-icons';

import { PublicBoardTopBarAncestors } from '~/components/v2/components/board/PublicBoardTopBarAncestors';
import { usePublicShowcaseCurrentSubPage } from '~/components/v2/components/showcase/hooks/usePublicShowcaseCurrentSubPage';
import { useBoardLocationBreadcrumbs } from '~/hooks/useBoardLocationBreadcrumbs';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

/**
 * The board topbar breadcrumbs for a board rendered INSIDE a collection link — both the real public
 * collection-board route (`/a/{shortId}/b/{boardId}`) and the collection link editor's Edit tab
 * (which has no `/a/…` URL). It prepends the collection crumb (and, when the board was reached from a
 * sub-page, that sub-page's crumb) to the board's own ancestor trail:
 * {Collection} / {Sub-page} / {…ancestors} / {Board}.
 *
 * This is the COLLECTION area's own builder — it binds the collection-context reads
 * (`usePublicShowcaseContext`, `usePublicShowcaseCurrentSubPage`) and the collection-page crumbs
 * that the shared board tree must never carry; navigation itself goes through the collection
 * tree's own PublicShowcaseProvider contract. PublicShowcaseBoardView composes it into the
 * shared board topbar as the `breadcrumbs` slot; the standalone board share supplies its own
 * board-only builder instead. Keeping the collection reads and the host navigation here — not in the
 * shared board leaf — is the whole point of FOUND-7: one collection-owned tree per route, composition
 * over "am I in a collection?" leaf checks.
 */
export const PublicShowcaseBoardBreadcrumbs = memo(() => {
  const { collection, onNavigateToBoard, onSelectPage } = usePublicShowcaseContext();
  // The sub-page this board was drilled into from — the URL surfaces carry it as the board route's
  // `?page={slug}` trail, the editor as its active page (the contract's activePage either way).
  // Null for a board reached from the collection root, so that trail is just
  // {Collection} / {…ancestors} / {Board}.
  const currentSubPage = usePublicShowcaseCurrentSubPage();

  // Return to the link's root page body. Selecting a page exits the drilled board on every surface
  // — the URL surfaces because the `/b/{boardId}` segment dies with the path, the editor because
  // setActivePage clears activeBoardId (see ShowcaseEditorNavigationProvider).
  const goToPublicShowcase = useCallback(() => onSelectPage(null), [onSelectPage]);

  const goToSubPage = useCallback(() => {
    if (currentSubPage) {
      onSelectPage({ pageId: currentSubPage.subPage.collectionId, pageSlug: currentSubPage.slug });
    }
  }, [currentSubPage, onSelectPage]);

  // Inside a collection the collection crumb (from the tree's required PublicShowcaseProvider)
  // always leads the trail, so the board icon is suppressed.
  const boardItems = useBoardLocationBreadcrumbs({
    shouldShowBoardIcon: false,
    onBoardClick: (board) => void onNavigateToBoard({ boardId: board.id, boardTitle: board.title }),
  });

  const items = useMemo(() => {
    const nextItems: BreadcrumbsItemType[] = [
      {
        id: collection.id,
        label: collection.name,
        prefix: <LinkIcon className="text-gray-10" size="md" />,
        className: 'max-w-[170px]',
        onClick: goToPublicShowcase,
        type: 'button',
      },
    ];

    // A board reached from a sub-page gets that page's crumb between the collection root and the
    // board's own ancestor trail: {Collection} / {Sub-page} / {…ancestors} / {Board}. Only the
    // collection root and the board are present otherwise.
    if (currentSubPage) {
      nextItems.push({
        id: `sub-page-${currentSubPage.slug}`,
        label: currentSubPage.subPage.name,
        className: 'max-w-[170px]',
        onClick: goToSubPage,
        type: 'button',
      });
    }

    nextItems.push(...boardItems);

    return nextItems;
  }, [boardItems, collection.id, collection.name, currentSubPage, goToPublicShowcase, goToSubPage]);

  return <PublicBoardTopBarAncestors items={items} />;
});

PublicShowcaseBoardBreadcrumbs.displayName = 'PublicShowcaseBoardBreadcrumbs';
